import { useEffect, useMemo, useRef, useState } from 'react';
import {
   ORDER_TYPE_LIMIT, DURATION_IMMEDIATE, DURATION_DAY, DURATION_GTC,
   GetMinStep, SELL_SIDE, Get10Power, CODE_HINT, ACCOUNT_HINT,
   PRICE_DECIMALS, QTY_DECIMALS, ShowError, confirmationDialog,
   JSON_KEY_INSTRUMENT, JSON_KEY_TRADING_ACCOUNT, JSON_KEY_SPECIAL_TYPE, JSON_KEY_DURATION, JSON_KEY_VERB,
   JSON_KEY_ORDER_TYPE, JSON_KEY_PRICE, JSON_KEY_VISIBLE_QTY, JSON_KEY_IN_SEQ, JSON_KEY_USER, 
   EVENT_TOPIC_FOR_TRANSACTION_SERVER, EVENT_TYPE_NEW_ORDER, JSON_KEY_MESSAGE_TYPE,
   JSON_KEY_ORIGINAL_QTY, DIALOG_BACKGROUND_BUY, DIALOG_BACKGROUND_SELL,
   HIDDEN, IMMEDIATE,
   FOK,
   DROPDOWN_LIST_NONE_ID,
   ORDER_TYPE_MARKET,
   LIMIT,
   DROPDOWN_LIST_NONE,
   DURATION_SESSION,
   JSON_KEY_TRIGGER_PRICE,
   JSON_KEY_TRIGGER_CONDITION,
   JSON_KEY_ORDER_TYPE_FLAGS,
   TRIGGER_FLAG,
   SCHEDULE_FLAG,
   JSON_KEY_SESSION_TYPE,
   TRIGGER_PRICE_LESS_THAN_OR_EQUAL_TO_BID_PRICE,
   SESSION_TYPE_NOT_SET,
   JSON_KEY_TRIGGER_DURATION
} from '../../../common/common';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { makeGetCellValue, selectSeqNum, selectUserId } from '../../../redux/globalsSlice';
import { DispatchTransactionEvent } from '../../events/events';
import { MSGTYPE_ORDER_NEW } from '../../../common/msg_types';

export const useOrderForm = () => {
   const { register, handleSubmit, formState: { errors } } = useForm();

   const [code, setCode] = useState('');
   const [tradingAccount, setTradingAccount] = useState('');
   const [price_field, setPriceField] = useState('');
   const [qty_field, setQtyField] = useState('');
   const [visible_qty_field, setVisibleQtyField] = useState('');
   const [orderType, setOrderType] = useState(LIMIT);
   const [fok, setFOK] = useState(false);
   const [hidden, setHidden] = useState(false);
   const [durationType, setDurationType] = useState(DURATION_DAY);
   const [triggerDurationType, setTriggerDurationType] = useState(DURATION_DAY);
   const [specialType, setSpecialType] = useState(DROPDOWN_LIST_NONE_ID);
   const [triggerCondition, setTriggerCondition] = useState(TRIGGER_PRICE_LESS_THAN_OR_EQUAL_TO_BID_PRICE);
   const [triggerPrice, setTriggerPrice] = useState('');
   const [session, setSession] = useState(SESSION_TYPE_NOT_SET);
   const [buySell, setBuySell] = useState(SELL_SIDE);
   const [tradingAccountOptions, setTradingAccountOptions] = useState([]);
   const [codeOptions, setCodeOptions] = useState([]);
   const [qty_step, setQtyStep] = useState(0.0001);
   const [price_step, setPriceStep] = useState(0.0001);
   const getDecimalValue = useMemo(() => makeGetCellValue(), []);
   const price_dec = useSelector((state) => getDecimalValue(state, code, PRICE_DECIMALS));
   const qty_dec = useSelector((state) => getDecimalValue(state, code, QTY_DECIMALS));
   const [triggerOrder, setTriggerOrder] = useState(false);
   const [scheduleOrder, setScheduleOrder] = useState(false);


   useEffect(() => {
      if (price_dec !== undefined) {
        setPriceStep(GetMinStep(price_dec));
      }
    }, [price_dec]);
    
   useEffect(() => {
      if (qty_dec !== undefined) {
         setQtyStep(GetMinStep(qty_dec));
      }
   }, [qty_dec]);

   const inSeqNum = useSelector(selectSeqNum);
   const inSeqNumRef = useRef(inSeqNum);
   useEffect(() => {       // to get the latest seqnum
      inSeqNumRef.current = inSeqNum;
   }, [inSeqNum]);
   
   const userId = useSelector(selectUserId);

   const onSubmit = async () => {
      // console.log('Form submitted, code:', code, ', tradingAccount:', tradingAccount, ', price:', price_field, ', qty:', qty_field, ', visible_qty:', visible_qty_field, ', orderType:', orderType, ', fok:', fok, ', hidden:', hidden, ', durationType:', durationType, ', buySell:', buySell);
      if ((code === CODE_HINT) || (code === '')) {
         ShowError("Instrument Code is missing");
         return;      
      }

      if ((tradingAccount === ACCOUNT_HINT) || (tradingAccount === '')) {
         ShowError("Account is missing");
         return;      
      }

      if (orderType === LIMIT) {
         if (price_field === '') {
            ShowError("Limit order must have a price");
            return;
         }

         if (qty_field === '') {
            ShowError("Quantity is missing");
            return;
         }

         if (hidden) {
            if (visible_qty_field === '') {
               ShowError("Hidden order is missing visible quantity");
               return;
            }

            if (parseFloat(visible_qty_field) >= parseFloat(qty_field)) {
               ShowError("Visible quantity must be less than quantity");
               return;
            }
         }
      }

      if (fok && durationType !== IMMEDIATE) {
         ShowError("FOK must have duration of immediate");
      }

      // trigger order checks      
      if (triggerOrder) {
         if (!triggerPrice || triggerPrice === '') {
               ShowError("Trigger order is missing trigger price");
               return;
         }
         
         if (parseFloat(triggerPrice) <= 0) {
               ShowError("Trigger price must be greater than zero");
               return;
         }

         if (!triggerCondition || triggerCondition === '' || triggerCondition === DROPDOWN_LIST_NONE) {
            console.log('triggerCondition: ', triggerCondition);
            ShowError("Missing trigger condition, ", triggerCondition);
            return;
         }
      }

      // session order checks
      if (scheduleOrder) {
         if (!session || session === '' || session === SESSION_TYPE_NOT_SET) {
               ShowError("Please select a session type (e.g., Preopen, Open, Close, Auction)");
               return;
         }         
      }

      // convert to integer for price and qty
      const rounderPrice = Math.round(price_field * Get10Power(price_dec));
      const roundQuantity = Math.round(qty_field * Get10Power(qty_dec));
      const roundVisibleQty = Math.round(visible_qty_field * Get10Power(qty_dec));

      const new_order = {
         [JSON_KEY_MESSAGE_TYPE]: MSGTYPE_ORDER_NEW,
         [JSON_KEY_INSTRUMENT]: code,
         [JSON_KEY_TRADING_ACCOUNT]: tradingAccount,
         [JSON_KEY_VERB]: buySell,
         [JSON_KEY_DURATION]: durationType,    
         [JSON_KEY_ORDER_TYPE]: orderType,
         [JSON_KEY_ORIGINAL_QTY]: roundQuantity,
         [JSON_KEY_VISIBLE_QTY]: roundVisibleQty,
         [JSON_KEY_IN_SEQ]: Number(inSeqNumRef.current),
         [JSON_KEY_USER]: userId,
      };

      if (triggerOrder) {
         const rounderTriggerPrice = Math.round(triggerPrice * Get10Power(price_dec));
         new_order[JSON_KEY_TRIGGER_PRICE] = rounderTriggerPrice;
         new_order[JSON_KEY_TRIGGER_CONDITION] = triggerCondition;
         new_order[JSON_KEY_TRIGGER_DURATION] = triggerDurationType;    
         new_order[JSON_KEY_ORDER_TYPE_FLAGS] = TRIGGER_FLAG;
      }

      if (scheduleOrder) {
         new_order[JSON_KEY_SESSION_TYPE] = session;
         new_order[JSON_KEY_ORDER_TYPE_FLAGS] = (new_order[JSON_KEY_ORDER_TYPE_FLAGS] || 0) | SCHEDULE_FLAG;
      }

      if (orderType === LIMIT) {
         new_order[JSON_KEY_PRICE] = rounderPrice;
      }

      if (fok) {
         new_order[JSON_KEY_SPECIAL_TYPE] = FOK;
      }
      else if (hidden) {
         new_order[JSON_KEY_SPECIAL_TYPE] = HIDDEN;
      }

      // build confirmation message
      let message = '';
      if (orderType === LIMIT) {
         message += ORDER_TYPE_LIMIT;
      } else {
         message += ORDER_TYPE_MARKET;
      }

      if (hidden) message += ', Hidden';

      if (fok) message += ' FOK';
      
      message += ' order';

      if (orderType === LIMIT) {
         message += ' with price ' + price_field;
      }

      message += ' , quantity ' + qty_field;

      if (hidden) {
         message += ' , visible quantity ' + visible_qty_field;
      }

      switch (durationType) {
         case DURATION_IMMEDIATE:
            message += ' and duration Immediate';
            break;
         case DURATION_DAY:
            message += ' and duration Day';
            break;
         case DURATION_GTC:
            message += ' and duration GTC';
            break;   
         case DURATION_SESSION:
            message += ' and duration Session';
            break;   
         default:
            message += ' and duration Unknown';
            break;
      }

      // confirmation check
      const confirmed = await confirmationDialog({
         title: code + (buySell === SELL_SIDE ? ' Sell Order' : ' Buy Order'),
         text: message,
         background: (buySell === SELL_SIDE ? DIALOG_BACKGROUND_SELL : DIALOG_BACKGROUND_BUY),
      });
   
      if (confirmed) {
         // console.log('Submitting new order:', new_order);
         setQtyField('');
         DispatchTransactionEvent(EVENT_TOPIC_FOR_TRANSACTION_SERVER, EVENT_TYPE_NEW_ORDER, new_order); 
      } 
   };

   return {
      code, setCode, codeOptions, setCodeOptions,
      tradingAccount, setTradingAccount,
      price_field, setPriceField,
      qty_field, setQtyField,
      visible_qty_field, setVisibleQtyField,
      orderType, setOrderType,
      fok, setFOK,
      hidden, setHidden,
      durationType, setDurationType,
      triggerDurationType, setTriggerDurationType,
      triggerCondition, setTriggerCondition,
      triggerPrice, setTriggerPrice,
      session, setSession,
      specialType, setSpecialType,
      buySell, setBuySell,
      onSubmit,
      tradingAccountOptions, setTradingAccountOptions,
      register, handleSubmit, errors,
      price_dec, qty_dec, price_step, qty_step,
      triggerOrder, setTriggerOrder,
      scheduleOrder, setScheduleOrder,
   };
};

